/**
 * 问题列表（输出窗口的数据）—— 纯函数，零 Vue 依赖。
 *
 * ## 刻意**不是**日志
 *
 * 第一版做成了追加式的日志（带时间戳、合并重复、500 条上限）。
 * 那是错的：求值每改一次就重跑一遍，于是「历史」里堆的全是
 * **同一批问题的旧快照** —— 用户看到十几行，其中大部分早就修好了。
 *
 * 现在它只是一份**当前诊断的视图**：图变 → 诊断重算 → 列表跟着变。
 * 修好的问题自己就消失了，不需要「清空」，也不需要时间戳
 * （所有条目的时间都是「此刻」）。
 *
 * 所以这里不再有 push / clear / 上限 / 合并计数 —— 那些都是在
 * 解决一个不该存在的问题。
 */

export type LogLevel = 'info' | 'warn' | 'error'

export interface Problem {
  level: LogLevel
  /** 来源：求值 / 校验 */
  source: string
  message: string
  /** 相关节点 —— 点一下能定位过去 */
  nodeId?: string
}

/** 诊断的 severity（求值层 / 校验层共用的那套）→ 面板的级别 */
export function levelOf(severity: string): LogLevel {
  return severity === 'error' ? 'error' : severity === 'warning' ? 'warn' : 'info'
}

/** 各级别的条数（面板标题上的角标） */
export function countByLevel(
  problems: readonly Problem[],
): Record<LogLevel, number> {
  const counts: Record<LogLevel, number> = { info: 0, warn: 0, error: 0 }
  for (const problem of problems) counts[problem.level] += 1
  return counts
}

/** 按级别过滤。`'all'` 不过滤 */
/**
 * 按「哪些等级被隐藏」过滤。
 *
 * ⚠️ 语义和 `filterProblems` **相反**：那个是「只留这一个等级」，
 * 这个是「去掉这几个等级」—— 因为界面改成了**多选点亮**
 * （用户要求：「点亮筛选就是显示，默认点亮，还能少一个全部筛选按钮」）。
 * 「全部」那个按钮在「默认全亮」的模型里是多余的：默认状态就是全部。
 */
export function hideProblems(
  problems: readonly Problem[],
  hidden: ReadonlySet<LogLevel>,
): Problem[] {
  if (hidden.size === 0) return [...problems]
  return problems.filter((p) => !hidden.has(p.level))
}

/** 哪些等级**当前有内容** —— 只给有内容的那几个显示筛选按钮 */
export function presentLevels(
  counts: Record<LogLevel, number>,
): LogLevel[] {
  return (['error', 'warn', 'info'] as LogLevel[]).filter((level) => counts[level] > 0)
}

/**
 * 这些等级里，**哪几个正亮着**（= 显示中）—— 即 `levels` 减去 `hidden`。
 *
 * 抽出来的理由是一次真机反馈：「输出窗口的筛选按钮没有高亮，列表里却有数据」。
 * 这条规则埋在组件的 `isLit` 闭包里时只能靠肉眼看；抽成纯函数之后
 * `verify:log` 能直接断言"**有数据就必须有亮着的按钮**"。
 *
 * ⚠️ 判据是"出现过 **且** 没被隐藏"，不是"没被隐藏" —— count 为 0 的等级
 * 压根不渲染按钮，把它算成"亮着"没有意义（两个集合别混）。
 */
export function litLevels(
  levels: readonly LogLevel[],
  hidden: ReadonlySet<LogLevel>,
): LogLevel[] {
  return levels.filter((level) => !hidden.has(level))
}

export function filterProblems(
  problems: readonly Problem[],
  level: LogLevel | 'all',
): Problem[] {
  if (level === 'all') return [...problems]
  return problems.filter((problem) => problem.level === level)
}

export function hasErrors(problems: readonly Problem[]): boolean {
  return problems.some((problem) => problem.level === 'error')
}
